import { setChallengeClockOffsetMs } from '@therame/ui';
import { useQuery } from '@tanstack/vue-query';
import {
  challengeKeys,
  challengeProgressPollInterval,
  challengeQueries,
} from '@therame/api-client';
import { ChallengeTaskState } from '@therame/shared';
import { computed, ref, watch } from 'vue';

/**
 * Shared Challenge-Mode panel state.
 *
 * Two surfaces drive the same panel:
 *   - desktop: a floating pill (ChallengePanel.vue) that expands in place.
 *   - mobile:  a contextual 5th item in BottomMenuBar that expands the same
 *     overlay (the floating pill is hidden below `tabletLandscape`).
 *
 * `expanded` is a module-level singleton so whichever surface toggles it, both
 * stay in sync. The progress query is keyed by sessionId, so multiple
 * `useQuery` callers share one cache entry via vue-query dedup.
 */
const expanded = ref(false);

export function useChallengePanel() {
  const isChallengeActive = window.localStorage.getItem('challenge_active') === 'true';
  const sessionId = window.localStorage.getItem('challenge_session_id');

  const sessionIdRef = computed(() => (isChallengeActive ? sessionId : null));

  const progressQuery = useQuery({
    queryKey: computed(() =>
      sessionIdRef.value ? challengeKeys.progress(sessionIdRef.value) : ['challenge', 'inactive'],
    ),
    queryFn: () => challengeQueries.progress(sessionIdRef.value!),
    enabled: computed(() => sessionIdRef.value !== null),
    refetchInterval: (q) => challengeProgressPollInterval(q.state.data),
    refetchOnWindowFocus: true,
  });

  const progress = progressQuery.data;
  const visible = computed(() => sessionIdRef.value !== null);

  // Mirror the server-authoritative challenge clock offset onto useSystemTime
  // whenever a fresh /challenge/progress poll lands. Multiple consumers of this
  // composable will each register a watcher; they all write the same scalar to
  // a module-level ref, so the redundancy is harmless.
  watch(
    () => progress.value?.clockOffsetMs,
    (offsetMs) => {
      if (typeof offsetMs === 'number') setChallengeClockOffsetMs(offsetMs);
    },
    { immediate: true },
  );

  const totalCount = computed(() => progress.value?.tasks.length ?? 0);
  const completedCount = computed(
    () => progress.value?.tasks.filter((t) => t.state === ChallengeTaskState.COMPLETED).length ?? 0,
  );
  const isComplete = computed(() => progress.value?.activeTaskId === null);

  const toggle = (): void => {
    expanded.value = !expanded.value;
  };
  const close = (): void => {
    expanded.value = false;
  };

  return {
    expanded,
    progress,
    visible,
    totalCount,
    completedCount,
    isComplete,
    toggle,
    close,
  };
}
